import { useState, useEffect } from 'react'
import { getWorkOrders, updateWorkOrder, getWorkOrderStats, getPhoneCallLogsByWorkOrder } from '../../lib/workOrders'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Search,
  Download,
  ChevronDown,
  AlertCircle,
  Clock,
  CheckCircle,
  Pause,
  X,
  Phone,
  Pencil
} from 'lucide-react'

const StatCard = ({ title, value, icon: Icon, color = 'primary' }) => {
  const colors = {
    primary: 'bg-rdc-primary/10 text-rdc-primary',
    orange: 'bg-rdc-orange/20 text-rdc-orange',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600'
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-rdc-taupe text-sm">{title}</span>
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-black">{value}</p>
    </div>
  )
}

const PriorityBadge = ({ priority }) => {
  const styles = {
    urgent: 'bg-red-100 text-red-700 border border-red-200',
    high: 'bg-amber-100 text-amber-700 border border-amber-200',
    normal: 'bg-blue-100 text-blue-700 border border-blue-200',
    low: 'bg-gray-100 text-gray-700 border border-gray-200'
  }

  const icons = {
    urgent: '🔴',
    high: '🟡',
    normal: '🔵',
    low: '⚪'
  }

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${styles[priority] || styles.normal}`}>
      <span>{icons[priority]}</span>
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  )
}

const TypeBadge = ({ type }) => {
  const typeLabels = {
    service_inquiry: 'Service Inquiry',
    transport: 'Transport',
    pickup_delivery: 'Pickup/Delivery',
    maintenance: 'Maintenance',
    storage: 'Storage',
    other: 'Other'
  }

  return (
    <span className="inline-block px-2 py-1 bg-rdc-cream text-rdc-dark-gray text-xs rounded-md font-medium">
      {typeLabels[type] || type}
    </span>
  )
}

const StatusSelect = ({ status, onchange, workOrderId }) => {
  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-600' },
    { value: 'in_progress', label: 'In Progress', icon: Pause, color: 'text-blue-600' },
    { value: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-600' },
    { value: 'cancelled', label: 'Cancelled', icon: X, color: 'text-gray-600' }
  ]

  return (
    <select
      value={status}
      onChange={(e) => onchange(workOrderId, e.target.value)}
      className={`px-3 py-1 text-xs font-medium rounded-full border-0 cursor-pointer ${
        status === 'pending'
          ? 'bg-yellow-100 text-yellow-700'
          : status === 'in_progress'
          ? 'bg-blue-100 text-blue-700'
          : status === 'completed'
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {statusOptions.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  )
}

const WorkOrderCard = ({ workOrder, onStatusChange, onExpandPhoneLogs, onEdit }) => {
  const [expanded, setExpanded] = useState(false)
  const [expandedPhoneLogs, setExpandedPhoneLogs] = useState(false)
  const [phoneLogs, setPhoneLogs] = useState([])
  const [loadingPhoneLogs, setLoadingPhoneLogs] = useState(false)

  const handleExpandPhoneLogs = async () => {
    if (expandedPhoneLogs) {
      setExpandedPhoneLogs(false)
      return
    }

    if (phoneLogs.length === 0) {
      setLoadingPhoneLogs(true)
      try {
        const { data } = await getPhoneCallLogsByWorkOrder(workOrder.id)
        setPhoneLogs(data || [])
      } catch (err) {
        console.error('Error loading phone logs:', err)
      } finally {
        setLoadingPhoneLogs(false)
      }
    }

    setExpandedPhoneLogs(true)
  }

  return (
    <div className="bg-white rounded-xl p-6 border border-rdc-warm-gray/30 shadow-sm hover:shadow-md transition-shadow">
      {/* Header with Priority and Type */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <PriorityBadge priority={workOrder.priority} />
            <TypeBadge type={workOrder.type} />
          </div>
          <h3 className="text-lg font-semibold text-black">{workOrder.title}</h3>
        </div>
      </div>

      {/* Client and Vehicle Info */}
      <div className="mb-4 space-y-2">
        {workOrder.client && (
          <p className="text-sm text-rdc-taupe">
            <span className="font-medium text-rdc-dark-gray">Client:</span> {workOrder.client.full_name}
          </p>
        )}
        {workOrder.vehicle && (
          <p className="text-sm text-rdc-taupe">
            <span className="font-medium text-rdc-dark-gray">Vehicle:</span> {workOrder.vehicle.year} {workOrder.vehicle.make} {workOrder.vehicle.model}
          </p>
        )}
      </div>

      {/* Description */}
      {workOrder.description && (
        <div className="mb-4 pb-4 border-b border-rdc-warm-gray/30">
          <p className="text-sm text-rdc-dark-gray line-clamp-2">
            {workOrder.description}
          </p>
        </div>
      )}

      {/* AI Summary Section */}
      {workOrder.ai_summary && (
        <div className="mb-4 p-4 bg-rdc-cream rounded-lg border border-rdc-primary/20">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-rdc-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-rdc-primary mb-1">AI Summary</p>
              <p className="text-sm text-rdc-dark-gray">{workOrder.ai_summary}</p>
            </div>
          </div>
        </div>
      )}

      {/* Provider Info */}
      {workOrder.provider_name && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-rdc-taupe">
            <span className="font-medium text-rdc-dark-gray">Provider:</span> {workOrder.provider_name}
          </p>
          {workOrder.provider_phone && (
            <p className="text-sm text-rdc-taupe flex items-center gap-1">
              <Phone size={14} />
              {workOrder.provider_phone}
            </p>
          )}
        </div>
      )}

      {/* Timestamp */}
      <div className="mb-4 text-xs text-rdc-taupe">
        Created {formatDistanceToNow(new Date(workOrder.created_at), { addSuffix: true })}
      </div>

      {/* Status and Actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <label className="text-xs text-rdc-taupe">Status</label>
          <StatusSelect
            status={workOrder.status}
            onchange={onStatusChange}
            workOrderId={workOrder.id}
          />
        </div>

        <button
          onClick={() => onEdit(workOrder)}
          className="px-4 py-2 text-sm text-rdc-primary hover:bg-rdc-primary/10 rounded-lg transition-colors flex items-center gap-2"
        >
          <Pencil size={16} />
          Edit
        </button>

        <button
          onClick={() => setExpanded(!expanded)}
          className="px-4 py-2 text-sm text-rdc-primary hover:bg-rdc-primary/10 rounded-lg transition-colors"
        >
          {expanded ? 'Hide Notes' : 'Show Notes'}
        </button>

        <button
          onClick={handleExpandPhoneLogs}
          className="px-4 py-2 text-sm text-rdc-dark-gray hover:bg-rdc-cream rounded-lg transition-colors flex items-center gap-2"
        >
          <Phone size={16} />
          Calls ({(phoneLogs || []).length})
        </button>
      </div>

      {/* Expanded Notes Section */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-rdc-warm-gray/30">
          <label className="block text-sm font-medium text-rdc-dark-gray mb-2">Notes</label>
          <textarea
            defaultValue={workOrder.notes || ''}
            className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
            rows="3"
            placeholder="Add notes about this work order..."
          />
        </div>
      )}

      {/* Phone Logs Section */}
      {expandedPhoneLogs && (
        <div className="mt-4 pt-4 border-t border-rdc-warm-gray/30">
          <h4 className="font-semibold text-rdc-dark-gray mb-3 flex items-center gap-2">
            <Phone size={16} className="text-rdc-primary" />
            Phone Call Logs
          </h4>
          {loadingPhoneLogs ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-rdc-primary border-t-transparent mx-auto" />
            </div>
          ) : phoneLogs.length === 0 ? (
            <p className="text-sm text-rdc-taupe text-center py-4">No phone calls recorded</p>
          ) : (
            <div className="space-y-2">
              {phoneLogs.map(log => (
                <div key={log.id} className="p-3 bg-rdc-cream rounded-lg text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-rdc-dark-gray">{log.provider_name}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      log.status === 'completed' ? 'bg-green-100 text-green-700' :
                      log.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {log.status}
                    </span>
                  </div>
                  <p className="text-rdc-taupe text-xs">
                    {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AdminWorkOrders() {
  const [workOrders, setWorkOrders] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [filterPriority, setFilterPriority] = useState('all')
  const [editingOrder, setEditingOrder] = useState(null)
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [ordersRes, statsRes] = await Promise.all([
        getWorkOrders(),
        getWorkOrderStats()
      ])

      if (ordersRes.error) throw ordersRes.error
      if (statsRes.error) throw statsRes.error

      setWorkOrders(ordersRes.data || [])
      setStats(statsRes.data)
    } catch (err) {
      console.error('Error loading work orders:', err)
      setError('Failed to load work orders')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { error } = await updateWorkOrder(id, { status: newStatus })
      if (error) throw error

      // Update local state
      setWorkOrders(prev =>
        prev.map(w => (w.id === id ? { ...w, status: newStatus } : w))
      )

      // Refresh stats
      const { data: newStats } = await getWorkOrderStats()
      if (newStats) setStats(newStats)
    } catch (err) {
      console.error('Error updating status:', err)
      setError('Failed to update status')
    }
  }

  const handleEditOpen = (workOrder) => {
    setEditingOrder(workOrder)
    setEditData({
      title: workOrder.title,
      description: workOrder.description || '',
      status: workOrder.status,
      priority: workOrder.priority,
      provider_name: workOrder.provider_name || '',
      provider_phone: workOrder.provider_phone || '',
      notes: workOrder.notes || ''
    })
  }

  const handleEditClose = () => {
    setEditingOrder(null)
    setEditData({})
  }

  const handleEditSave = async () => {
    if (!editingOrder) return

    try {
      setSaving(true)

      // Prepare data for API (convert to camelCase and handle completedAt)
      const updatePayload = {
        status: editData.status,
        priority: editData.priority,
        description: editData.description,
        providerName: editData.provider_name,
        providerPhone: editData.provider_phone,
        notes: editData.notes
      }

      // Auto-set completedAt when status changes to completed
      if (editData.status === 'completed' && editingOrder.status !== 'completed') {
        updatePayload.completedAt = new Date().toISOString()
      }

      const { error } = await updateWorkOrder(editingOrder.id, updatePayload)
      if (error) throw error

      // Update local state with all edited fields
      setWorkOrders(prev =>
        prev.map(w =>
          w.id === editingOrder.id
            ? {
                ...w,
                title: editData.title,
                description: editData.description,
                status: editData.status,
                priority: editData.priority,
                provider_name: editData.provider_name,
                provider_phone: editData.provider_phone,
                notes: editData.notes,
                ...(editData.status === 'completed' && editingOrder.status !== 'completed' && { completed_at: new Date().toISOString() })
              }
            : w
        )
      )

      // Refresh stats
      const { data: newStats } = await getWorkOrderStats()
      if (newStats) setStats(newStats)

      // Show success message
      setSuccessMessage('Work order updated successfully')
      setTimeout(() => setSuccessMessage(''), 3000)

      handleEditClose()
    } catch (err) {
      console.error('Error updating work order:', err)
      setError('Failed to update work order')
    } finally {
      setSaving(false)
    }
  }

  const exportToCSV = () => {
    const headers = ['ID', 'Title', 'Client', 'Vehicle', 'Type', 'Priority', 'Status', 'Created', 'Provider']
    const rows = filteredWorkOrders.map(w => [
      w.id,
      w.title,
      w.client?.full_name || '',
      w.vehicle ? `${w.vehicle.year} ${w.vehicle.make} ${w.vehicle.model}` : '',
      w.type,
      w.priority,
      w.status,
      format(new Date(w.created_at), 'yyyy-MM-dd HH:mm'),
      w.provider_name || ''
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `work-orders-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Filter work orders
  const filteredWorkOrders = workOrders.filter(wo => {
    const matchesSearch =
      wo.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wo.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wo.client?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wo.provider_name?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = filterStatus === 'all' || wo.status === filterStatus
    const matchesType = filterType === 'all' || wo.type === filterType
    const matchesPriority = filterPriority === 'all' || wo.priority === filterPriority

    return matchesSearch && matchesStatus && matchesType && matchesPriority
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-black">Work Orders</h1>
          <p className="text-rdc-taupe mt-1">Manage work orders created by the AI concierge</p>
        </div>

        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-rdc-warm-gray rounded-lg text-rdc-dark-gray hover:bg-rdc-cream transition-colors"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-sm underline">
            Dismiss
          </button>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
          <CheckCircle size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Pending"
            value={stats.byStatus.pending || 0}
            icon={Clock}
            color="orange"
          />
          <StatCard
            title="In Progress"
            value={stats.byStatus.in_progress || 0}
            icon={Pause}
            color="blue"
          />
          <StatCard
            title="Completed"
            value={stats.byStatus.completed || 0}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Total"
            value={stats.total}
            icon={AlertCircle}
            color="primary"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-rdc-taupe" />
            <input
              type="text"
              placeholder="Search by title, description, client, or provider..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="service_inquiry">Service Inquiry</option>
            <option value="transport">Transport</option>
            <option value="pickup_delivery">Pickup/Delivery</option>
            <option value="maintenance">Maintenance</option>
            <option value="storage">Storage</option>
            <option value="other">Other</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2 border border-rdc-warm-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Work Orders List */}
      {filteredWorkOrders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <AlertCircle className="w-12 h-12 text-rdc-warm-gray mx-auto mb-4" />
          <h3 className="font-semibold text-black mb-2">No work orders found</h3>
          <p className="text-rdc-taupe">
            {workOrders.length === 0
              ? 'Work orders will appear here'
              : 'Try adjusting your search or filters'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredWorkOrders.map(workOrder => (
            <WorkOrderCard
              key={workOrder.id}
              workOrder={workOrder}
              onStatusChange={handleStatusChange}
              onEdit={handleEditOpen}
            />
          ))}
        </div>
      )}

      {/* Count */}
      {filteredWorkOrders.length > 0 && (
        <p className="text-sm text-rdc-taupe mt-4">
          Showing {filteredWorkOrders.length} of {workOrders.length} work orders
        </p>
      )}

      {/* Edit Modal */}
      {editingOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-rdc-warm-gray/30 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-black">Edit Work Order</h2>
              <button
                onClick={handleEditClose}
                disabled={saving}
                className="text-rdc-taupe hover:text-black transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editData.title || ''}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                  disabled={saving}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Description
                </label>
                <textarea
                  value={editData.description || ''}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                  rows="3"
                  disabled={saving}
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Status
                </label>
                <select
                  value={editData.status || 'pending'}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                  disabled={saving}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Priority
                </label>
                <select
                  value={editData.priority || 'normal'}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                  disabled={saving}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Provider Name */}
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Provider Name
                </label>
                <input
                  type="text"
                  value={editData.provider_name || ''}
                  onChange={(e) => setEditData({ ...editData, provider_name: e.target.value })}
                  className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                  disabled={saving}
                />
              </div>

              {/* Provider Phone */}
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Provider Phone
                </label>
                <input
                  type="tel"
                  value={editData.provider_phone || ''}
                  onChange={(e) => setEditData({ ...editData, provider_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                  disabled={saving}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                  Notes
                </label>
                <textarea
                  value={editData.notes || ''}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-rdc-warm-gray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rdc-primary focus:border-transparent"
                  rows="3"
                  placeholder="Add notes about this work order..."
                  disabled={saving}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-rdc-warm-gray/30 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={handleEditClose}
                disabled={saving}
                className="px-4 py-2 text-sm text-rdc-dark-gray hover:bg-rdc-cream rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-rdc-primary text-white rounded-lg hover:bg-rdc-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
